import { useEffect, useRef } from 'react';
import { escapeLayerPhase, type EscapeLayerPhase } from '@odo/shared-ui/utils/escapeLayer';

// Document capture (bubble when a text field has focus) keeps the window-bubble modal out; inner
// layers on window capture go first. Closing waits a tick so a later listener can preventDefault.
export function useLayerEscape(
    onEscape: () => void,
    ownsEscape?: (event: KeyboardEvent) => boolean
): void {
    const handlerRef = useRef(onEscape);
    const ownsRef = useRef(ownsEscape);

    useEffect(() => {
        handlerRef.current = onEscape;
        ownsRef.current = ownsEscape;
    }, [onEscape, ownsEscape]);

    useEffect(() => {
        let timer: number | undefined;
        const claimIn = (phase: EscapeLayerPhase) => (event: KeyboardEvent) => {
            if (escapeLayerPhase(event) !== phase) return;
            if (ownsRef.current && !ownsRef.current(event)) return;
            event.stopPropagation();
            window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                if (!event.defaultPrevented) handlerRef.current();
            });
        };
        const onCapture = claimIn('capture');
        const onBubble = claimIn('bubble');
        document.addEventListener('keydown', onCapture, true);
        document.addEventListener('keydown', onBubble);
        return () => {
            document.removeEventListener('keydown', onCapture, true);
            document.removeEventListener('keydown', onBubble);
            window.clearTimeout(timer);
        };
    }, []);
}
